import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  LayoutDashboard, UserCog, Building, Building2, Upload, Download, ClipboardCheck,
  Settings2, Shield, FileText, Bell, Settings, Cable, BookOpen, Plug,
  Search, Loader2, Users, Wallet, Construction, Plus, Pencil, Trash2, X, RefreshCw, Archive,
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  getEhrTree, getEhrSyncStatus, syncFeishuRoster, archiveNonFeishuDepts,
  createEmployee, updateEmployee, deleteEmployee,
  createTalentUnit, updateTalentUnit,
} from '../api';
import {
  User, OrgUnit, Employee,
  EMPLOYEE_TYPE_LABELS,
} from '../types';

interface RosterData {
  units: OrgUnit[];
  employees: Employee[];
}

export type EhrSection = 'main' | 'data' | 'system';

interface Props {
  currentUser: User;
  section: EhrSection;
  notificationBell?: React.ReactNode;
}

interface TabDef {
  key: string;
  label: string;
  icon: React.ElementType;
}

const SECTION_TABS: Record<EhrSection, TabDef[]> = {
  main: [
    { key: 'dashboard', label: '首页看板', icon: LayoutDashboard },
    { key: 'persons', label: '人员管理', icon: UserCog },
    { key: 'legal', label: '公司主体', icon: Building },
    { key: 'departments', label: '部门管理', icon: Building2 },
  ],
  data: [
    { key: 'imports', label: '数据导入', icon: Upload },
    { key: 'exports', label: '数据导出', icon: Download },
    { key: 'approvals', label: '审批记录', icon: ClipboardCheck },
  ],
  system: [
    { key: 'field-configs', label: '字段配置', icon: Settings2 },
    { key: 'permissions', label: '权限管理', icon: Shield },
    { key: 'audit-logs', label: '审计日志', icon: FileText },
    { key: 'notifications', label: '通知中心', icon: Bell },
    { key: 'settings', label: '系统设置', icon: Settings },
    { key: 'integrations', label: 'API 集成', icon: Cable },
    { key: 'api-docs', label: 'API 文档', icon: BookOpen },
    { key: 'feishu-diag', label: '飞书诊断', icon: Plug },
  ],
};

const SECTION_META: Record<EhrSection, { title: string; icon: React.ElementType }> = {
  main: { title: '主要功能', icon: LayoutDashboard },
  data: { title: '数据管理', icon: Upload },
  system: { title: '系统管理', icon: Settings },
};

const EMP_STATUS_LABELS: Record<string, string> = {
  active: '在职', resigned: '离职', on_leave: '请假',
};
const EMP_STATUS_STYLES: Record<string, string> = {
  active: 'bg-primary-50 text-primary-600',
  resigned: 'bg-gray-100 text-gray-500',
  on_leave: 'bg-gray-50 text-gray-600',
};

function fmtMoney(n: number | null | undefined) {
  if (n == null) return '—';
  if (n >= 10000) return `¥${(n / 10000).toFixed(n >= 1000000 ? 0 : 1)}万`;
  return `¥${Math.round(n).toLocaleString()}`;
}

function fmtSyncTime(iso: string | null | undefined) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function EhrPage({ currentUser, section, notificationBell }: Props) {
  const canSeeCost = ['admin', 'hr', 'vip'].includes(currentUser.role);
  const tabs = SECTION_TABS[section];
  const [tab, setTab] = useState<string>(tabs[0].key);

  // Reset active tab when switching section
  useEffect(() => { setTab(SECTION_TABS[section][0].key); }, [section]);

  const canEdit = ['admin', 'hr', 'vip'].includes(currentUser.role);
  const [roster, setRoster] = useState<RosterData | null>(null);
  const [loading, setLoading] = useState(false);

  const needsRoster = section === 'main';
  const fetchRoster = useCallback(async () => {
    if (!needsRoster) return;
    setLoading(true);
    try {
      setRoster(await getEhrTree());
    } catch {
      toast.error('加载花名册数据失败');
    } finally {
      setLoading(false);
    }
  }, [needsRoster]);

  useEffect(() => { fetchRoster(); }, [fetchRoster]);

  // 飞书同步状态
  const [lastSync, setLastSync] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [nonFeishuActive, setNonFeishuActive] = useState(0);
  const [dupRefs, setDupRefs] = useState(0);
  const [archiving, setArchiving] = useState(false);
  const fetchSyncStatus = useCallback(async () => {
    if (!needsRoster) return;
    try {
      const s = await getEhrSyncStatus();
      setLastSync(s?.lastSync || null);
      setNonFeishuActive(s?.nonFeishuActive || 0);
      setDupRefs(s?.dupRefs || 0);
    } catch { /* ignore */ }
  }, [needsRoster]);
  useEffect(() => { fetchSyncStatus(); }, [fetchSyncStatus]);

  const handleArchiveNonFeishu = useCallback(async () => {
    if (!window.confirm(
      `将整理部门：\n` +
      `① 把 ${nonFeishuActive} 个「非花名册」组织节点（人才中枢旧部门 + 招聘虚位/影子岗位）归档移出；\n` +
      `② 把 ${dupRefs} 个指向「旧同名部门」的岗位/编制引用合并到飞书同名部门（修复“同部门却无可绑定编制”）。\n\n` +
      `数据不会物理删除，可在数据库恢复。确定继续？`
    )) return;
    setArchiving(true);
    const tid = toast.loading('正在整理部门…');
    try {
      const r = await archiveNonFeishuDepts();
      toast.success(`已归档 ${r?.archived ?? 0} 个（部门 ${r?.departments ?? 0}，招聘虚位 ${r?.shadow ?? 0}）；合并引用 ${r?.refsMoved ?? 0} 条`, { id: tid, duration: 5000 });
      await fetchSyncStatus();
      await fetchRoster();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || '归档失败', { id: tid });
    } finally {
      setArchiving(false);
    }
  }, [nonFeishuActive, dupRefs, fetchSyncStatus, fetchRoster]);

  const handleSyncFeishu = useCallback(async () => {
    setSyncing(true);
    const tid = toast.loading('正在从飞书人事同步…');
    try {
      const r = await syncFeishuRoster();
      const s = r?.stats || {};
      toast.success(
        `同步完成：部门 ${s.departments ?? 0}，人员 ${s.users ?? 0}（新增 ${s.empCreated ?? 0}，离职 ${s.departed ?? 0}）`,
        { id: tid, duration: 5000 }
      );
      setLastSync(r?.lastSync || null);
      await fetchRoster();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || '同步失败', { id: tid, duration: 6000 });
    } finally {
      setSyncing(false);
    }
  }, [fetchRoster]);

  // 花名册视图：与人才中枢共用底层数据，但 eHR 只看「真实组织」——
  // 剔除人才中枢的「影子岗位/待招聘虚位」(is_shadow) 与已归档单元。
  const rosterView = useMemo<RosterData | null>(() => {
    if (!roster) return null;
    return {
      units: (roster.units || []).filter(u => !u.is_shadow && u.status !== 'archived'),
      employees: roster.employees || [],
    };
  }, [roster]);
  const hiddenUnitCount = (roster?.units?.length || 0) - (rosterView?.units?.length || 0);

  const meta = SECTION_META[section];
  const MetaIcon = meta.icon;

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header + top tabs */}
      <div className="bg-white border-b border-gray-200 px-6 pt-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
              <MetaIcon size={16} className="text-primary-600" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-800">{meta.title}</h1>
              <p className="text-xs text-gray-400">eHR 花名册</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {needsRoster && canEdit && (
              <div className="flex items-center gap-2">
                {lastSync && (
                  <span className="text-xs text-gray-400">上次同步 {fmtSyncTime(lastSync)}</span>
                )}
                {(nonFeishuActive > 0 || dupRefs > 0) && (
                  <button
                    onClick={handleArchiveNonFeishu}
                    disabled={archiving}
                    title="把非花名册的旧部门与招聘虚位归档移出、并把指向旧同名部门的岗位/编制引用合并到飞书部门，使编制/岗位下拉与绑定关系与花名册一致"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 disabled:opacity-60 rounded-lg transition-colors"
                  >
                    <Archive size={14} className={archiving ? 'animate-pulse' : ''} />
                    {archiving ? '整理中…' : `整理部门 (${nonFeishuActive + dupRefs})`}
                  </button>
                )}
                <button
                  onClick={handleSyncFeishu}
                  disabled={syncing}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-60 rounded-lg transition-colors"
                >
                  <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} />
                  {syncing ? '同步中…' : '从飞书人事同步'}
                </button>
              </div>
            )}
            {notificationBell}
          </div>
        </div>
        <div className="flex items-center gap-1 -mb-px overflow-x-auto">
          {tabs.map(t => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={clsx(
                  'flex items-center gap-1.5 px-3.5 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                  active
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                )}
              >
                <Icon size={15} />{t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center py-24 text-gray-400">
            <Loader2 size={22} className="animate-spin mr-2" />加载中…
          </div>
        ) : (
          <>
            {section === 'main' && tab === 'dashboard' && <DashboardTab tree={rosterView} canSeeCost={canSeeCost} />}
            {section === 'main' && tab === 'persons' && <PersonsTab tree={rosterView} canSeeCost={canSeeCost} canEdit={canEdit} onChanged={fetchRoster} />}
            {section === 'main' && tab === 'departments' && <DepartmentsTab tree={rosterView} canSeeCost={canSeeCost} canEdit={canEdit} onChanged={fetchRoster} hiddenCount={hiddenUnitCount} />}
            {section === 'main' && tab === 'legal' && <LegalEntitiesTab />}
            {section !== 'main' && (
              <PlaceholderTab label={tabs.find(t => t.key === tab)?.label || ''} sectionKey={section} tabKey={tab} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── 首页看板 ────────────────────────────────────────────────────
function DashboardTab({ tree, canSeeCost }: { tree: RosterData | null; canSeeCost: boolean }) {
  const emps = tree?.employees || [];
  const units = (tree?.units || []).filter(u => u.status === 'active');
  const active = emps.filter(e => e.status === 'active');
  const onLeave = emps.filter(e => e.status === 'on_leave');
  const resigned = emps.filter(e => e.status === 'resigned');
  const totalCost = active.reduce((s, e) => s + (e.monthly_cost || 0), 0);

  const cards = [
    { label: '在职人数', value: active.length, icon: Users, tint: 'text-primary-600 bg-primary-50' },
    { label: '请假', value: onLeave.length, icon: UserCog, tint: 'text-gray-600 bg-gray-50' },
    { label: '离职', value: resigned.length, icon: UserCog, tint: 'text-gray-500 bg-gray-100' },
    { label: '部门数', value: units.length, icon: Building2, tint: 'text-primary-600 bg-primary-50' },
    ...(canSeeCost ? [{ label: '在职月成本', value: fmtMoney(totalCost), icon: Wallet, tint: 'text-primary-600 bg-primary-50' }] : []),
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {cards.map((c, i) => {
          const Icon = c.icon;
          return (
            <div key={i} className="bg-white rounded-xl border border-gray-100 p-5">
              <div className={clsx('w-9 h-9 rounded-lg flex items-center justify-center mb-3', c.tint)}>
                <Icon size={17} />
              </div>
              <div className="text-2xl font-bold text-gray-800">{c.value}</div>
              <div className="text-xs text-gray-400 mt-1">{c.label}</div>
            </div>
          );
        })}
      </div>
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">各部门人数</h3>
        {units.length === 0 ? (
          <EmptyHint text="暂无部门数据" />
        ) : (
          <div className="space-y-2">
            {units.map(u => {
              const count = emps.filter(e => e.org_unit_id === u.id && e.status === 'active').length;
              const max = Math.max(1, ...units.map(x => emps.filter(e => e.org_unit_id === x.id && e.status === 'active').length));
              return (
                <div key={u.id} className="flex items-center gap-3">
                  <div className="w-28 text-xs text-gray-600 truncate shrink-0">{u.name}</div>
                  <div className="flex-1 bg-gray-100 rounded-full h-2.5 overflow-hidden">
                    <div className="h-full bg-primary-400 rounded-full" style={{ width: `${(count / max) * 100}%` }} />
                  </div>
                  <div className="w-8 text-right text-xs font-medium text-gray-700">{count}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── 人员管理 ────────────────────────────────────────────────────
function PersonsTab({ tree, canSeeCost, canEdit, onChanged }: { tree: RosterData | null; canSeeCost: boolean; canEdit: boolean; onChanged: () => void }) {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [kw, setKw] = useState('');
  const [modalEmp, setModalEmp] = useState<Employee | 'new' | null>(null);
  const emps = tree?.employees || [];
  const units = tree?.units || [];
  const deptName = useMemo(() => {
    const m: Record<string, string> = {};
    units.forEach(u => { m[u.id] = u.name; });
    return m;
  }, [units]);
  // 从组织树祖先推导每个部门的「二级/三级」名（一级=留形总部，二级=研发部，三级=软件组）
  const deptLevels = useMemo(() => {
    const byId: Record<string, OrgUnit> = {};
    units.forEach(u => { byId[u.id] = u; });
    const m: Record<string, { l2: string; l3: string }> = {};
    units.forEach(u => {
      const chain: OrgUnit[] = [];
      const guard = new Set<string>();
      let cur: OrgUnit | undefined = u;
      while (cur && !guard.has(cur.id)) { guard.add(cur.id); chain.unshift(cur); cur = cur.parent_id ? byId[cur.parent_id] : undefined; }
      m[u.id] = { l2: chain[1]?.name || '', l3: chain[2]?.name || '' };
    });
    return m;
  }, [units]);

  const statusTabs = [
    { value: 'all', label: '在册' },
    { value: 'active', label: '在职' },
    { value: 'on_leave', label: '请假' },
    { value: 'resigned', label: '离职' },
  ];

  const filtered = emps.filter(e => {
    // 「在册」默认不含离职；离职人员单独在「离职」页查看。
    if (statusFilter === 'all') { if (e.status === 'resigned') return false; }
    else if (e.status !== statusFilter) return false;
    if (kw) {
      const q = kw.toLowerCase();
      return (e.name || '').toLowerCase().includes(q)
        || (e.title || '').toLowerCase().includes(q)
        || (deptName[e.org_unit_id || ''] || '').toLowerCase().includes(q);
    }
    return true;
  });

  const handleDelete = async (emp: Employee) => {
    if (!window.confirm(`确认删除员工「${emp.name}」？此操作不可恢复。`)) return;
    try {
      await deleteEmployee(emp.id);
      toast.success('已删除');
      onChanged();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || '删除失败');
    }
  };

  // 姓名/二级部门/三级部门/职位/职级/类型/入职日期 = 7 列
  const colCount = 7 + (canSeeCost ? 1 : 0) + (canEdit ? 1 : 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          {statusTabs.map(t => (
            <button
              key={t.value}
              onClick={() => setStatusFilter(t.value)}
              className={clsx(
                'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                statusFilter === t.value ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={kw}
              onChange={e => setKw(e.target.value)}
              placeholder="搜索姓名 / 职位 / 部门"
              className="pl-9 pr-3 py-2 w-56 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
          {canEdit && (
            <button
              onClick={() => setModalEmp('new')}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors whitespace-nowrap"
            >
              <Plus size={15} />新增员工
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-gray-500 text-xs">
              <th className="text-left font-medium px-4 py-3">姓名</th>
              <th className="text-left font-medium px-4 py-3">二级部门</th>
              <th className="text-left font-medium px-4 py-3">三级部门</th>
              <th className="text-left font-medium px-4 py-3">职位</th>
              <th className="text-left font-medium px-4 py-3">职级</th>
              <th className="text-left font-medium px-4 py-3">类型</th>
              <th className="text-left font-medium px-4 py-3">入职日期</th>
              {canSeeCost && <th className="text-right font-medium px-4 py-3">月成本</th>}
              <th className="text-left font-medium px-4 py-3">状态</th>
              {canEdit && <th className="text-right font-medium px-4 py-3">操作</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.length === 0 ? (
              <tr><td colSpan={colCount}><EmptyHint text={canEdit ? '暂无人员数据。点右上「新增员工」开始录入。' : '暂无人员数据。'} /></td></tr>
            ) : filtered.map(e => (
              <tr key={e.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center text-xs font-bold text-primary-600 shrink-0">
                      {e.name?.charAt(0) || '?'}
                    </div>
                    <span className="font-medium text-gray-800">{e.name}</span>
                    {!!e.is_leader && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-50 text-gray-600">负责人</span>}
                  </div>
                </td>
                <td className="px-4 py-3 text-gray-600">{deptLevels[e.org_unit_id || '']?.l2 || deptName[e.org_unit_id || ''] || '—'}</td>
                <td className="px-4 py-3 text-gray-500">{deptLevels[e.org_unit_id || '']?.l3 || '—'}</td>
                <td className="px-4 py-3 text-gray-600">{e.title || '—'}</td>
                <td className="px-4 py-3 text-gray-600">{e.level || '—'}</td>
                <td className="px-4 py-3 text-gray-600">{EMPLOYEE_TYPE_LABELS[e.employee_type] || e.employee_type}</td>
                <td className="px-4 py-3 text-gray-600">{e.hire_date || '—'}</td>
                {canSeeCost && <td className="px-4 py-3 text-right text-gray-600">{fmtMoney(e.monthly_cost)}</td>}
                <td className="px-4 py-3">
                  <span className={clsx('text-xs px-2 py-0.5 rounded-full', EMP_STATUS_STYLES[e.status] || 'bg-gray-100 text-gray-500')}>
                    {EMP_STATUS_LABELS[e.status] || e.status}
                  </span>
                </td>
                {canEdit && (
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setModalEmp(e)} className="p-1.5 rounded-md text-gray-400 hover:text-primary-600 hover:bg-primary-50" title="编辑"><Pencil size={14} /></button>
                      <button onClick={() => handleDelete(e)} className="p-1.5 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-50" title="删除"><Trash2 size={14} /></button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-gray-400">共 {filtered.length} 人</div>

      {modalEmp && (
        <EmployeeModal
          employee={modalEmp === 'new' ? null : modalEmp}
          units={units}
          onClose={() => setModalEmp(null)}
          onSaved={() => { setModalEmp(null); onChanged(); }}
        />
      )}
    </div>
  );
}

// ── 部门管理 ────────────────────────────────────────────────────
function DepartmentsTab({ tree, canSeeCost, canEdit, onChanged, hiddenCount }: { tree: RosterData | null; canSeeCost: boolean; canEdit: boolean; onChanged: () => void; hiddenCount?: number }) {
  const [modalUnit, setModalUnit] = useState<OrgUnit | 'new' | null>(null);
  const units = tree?.units || [];
  const emps = tree?.employees || [];
  const countOf = (uid: string) => emps.filter(e => e.org_unit_id === uid && e.status === 'active').length;
  const costOf = (uid: string) => emps.filter(e => e.org_unit_id === uid && e.status === 'active').reduce((s, e) => s + (e.monthly_cost || 0), 0);
  const colCount = 6 + (canSeeCost ? 1 : 0) + (canEdit ? 1 : 0);

  return (
    <div className="space-y-4">
      {!!hiddenCount && hiddenCount > 0 && (
        <div className="flex items-start gap-2 px-4 py-2.5 rounded-lg bg-primary-50 text-primary-700 text-xs">
          <Building2 size={14} className="mt-0.5 shrink-0" />
          <span>已隔离人才中枢的 <b>{hiddenCount}</b> 个「影子岗位/待招聘虚位」，此处仅显示真实部门。招聘编制/虚位请在「编制管理」「人才中枢」查看。</span>
        </div>
      )}
      {canEdit && (
        <div className="flex justify-end">
          <button
            onClick={() => setModalUnit('new')}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
          >
            <Plus size={15} />新增部门
          </button>
        </div>
      )}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-gray-500 text-xs">
              <th className="text-left font-medium px-4 py-3">部门名称</th>
              <th className="text-left font-medium px-4 py-3">上级部门</th>
              <th className="text-left font-medium px-4 py-3">负责人</th>
              <th className="text-right font-medium px-4 py-3">在职人数</th>
              <th className="text-right font-medium px-4 py-3">编制</th>
              {canSeeCost && <th className="text-right font-medium px-4 py-3">月成本</th>}
              <th className="text-left font-medium px-4 py-3">状态</th>
              {canEdit && <th className="text-right font-medium px-4 py-3">操作</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {units.length === 0 ? (
              <tr><td colSpan={colCount}><EmptyHint text={canEdit ? '暂无部门数据。点右上「新增部门」开始建立组织。' : '暂无部门数据。'} /></td></tr>
            ) : units.map(u => (
              <tr key={u.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-800">{u.name}</td>
                <td className="px-4 py-3 text-gray-600">{u.parent_name || '—'}</td>
                <td className="px-4 py-3 text-gray-600">{u.owner_name || '—'}</td>
                <td className="px-4 py-3 text-right text-gray-600">{countOf(u.id)}</td>
                <td className="px-4 py-3 text-right text-gray-600">{u.hc_plan ?? '—'}</td>
                {canSeeCost && <td className="px-4 py-3 text-right text-gray-600">{fmtMoney(costOf(u.id))}</td>}
                <td className="px-4 py-3">
                  <span className={clsx('text-xs px-2 py-0.5 rounded-full', u.status === 'active' ? 'bg-primary-50 text-primary-600' : 'bg-gray-100 text-gray-500')}>
                    {u.status === 'active' ? '启用' : '停用'}
                  </span>
                </td>
                {canEdit && (
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setModalUnit(u)} className="p-1.5 rounded-md text-gray-400 hover:text-primary-600 hover:bg-primary-50" title="编辑"><Pencil size={14} /></button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalUnit && (
        <DepartmentModal
          unit={modalUnit === 'new' ? null : modalUnit}
          units={units}
          onClose={() => setModalUnit(null)}
          onSaved={() => { setModalUnit(null); onChanged(); }}
        />
      )}
    </div>
  );
}

// ── 公司主体（ATS 暂无数据源） ──────────────────────────────────
function LegalEntitiesTab() {
  return (
    <PlaceholderCard
      icon={Building}
      title="公司主体"
      desc="多法律主体（发薪公司）管理规划中，将在 eHR 内建立法律主体档案后在此维护。"
    />
  );
}

// ── 通用占位（数据管理 / 系统管理各子项） ──────────────────────
function PlaceholderTab({ label, sectionKey, tabKey }: { label: string; sectionKey: EhrSection; tabKey: string }) {
  const descMap: Record<string, string> = {
    imports: '员工/部门批量导入（Excel）规划中。当前可在「人员管理」「部门管理」逐条新增。',
    exports: '花名册数据导出（Excel）规划中。',
    approvals: 'HR 业务审批（入职/异动/请假等）规划中；招聘编制审批见「编制管理」。',
    'field-configs': '人员/组织自定义字段配置规划中。',
    permissions: 'eHR 角色与数据权限规划中；账号权限见「团队管理」。',
    'audit-logs': '花名册访问审计已在后端 rosterService 记录（roster_access_logs），可视化查询规划中。',
    notifications: '通知中心规划中。',
    settings: 'eHR 系统设置规划中。',
    integrations: 'API 集成（对外开放花名册数据）规划中。',
    'api-docs': 'eHR 对外 API 文档规划中。',
    'feishu-diag': '飞书同步诊断规划中。',
  };
  return (
    <PlaceholderCard
      icon={SECTION_TABS[sectionKey].find(t => t.key === tabKey)?.icon || Construction}
      title={label}
      desc={descMap[tabKey] || '该模块规划中，敬请期待。'}
    />
  );
}

function PlaceholderCard({ icon: Icon, title, desc }: { icon: React.ElementType; title: string; desc: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-10 flex flex-col items-center justify-center text-center max-w-2xl mx-auto mt-8">
      <div className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center mb-4">
        <Icon size={26} className="text-gray-300" />
      </div>
      <h3 className="text-base font-semibold text-gray-700 mb-2">{title}</h3>
      <p className="text-sm text-gray-400 leading-relaxed">{desc}</p>
      <div className="mt-5 flex items-center gap-1.5 text-xs text-gray-500 bg-gray-50 px-3 py-1.5 rounded-lg">
        <Construction size={13} />规划中
      </div>
    </div>
  );
}

function EmptyHint({ text }: { text: string }) {
  return <div className="py-12 text-center text-sm text-gray-400">{text}</div>;
}

// ── 弹窗基座 / 表单辅助 ─────────────────────────────────────────
const inputCls = 'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400';

function ModalShell({ title, onClose, onSubmit, saving, children }: {
  title: string; onClose: () => void; onSubmit: () => void; saving: boolean; children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="p-5">{children}</div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">取消</button>
          <button onClick={onSubmit} disabled={saving} className="px-4 py-2 text-sm text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-60 flex items-center gap-1.5">
            {saving && <Loader2 size={14} className="animate-spin" />}保存
          </button>
        </div>
      </div>
    </div>
  );
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-500 mb-1">{label}</span>
      {children}
    </label>
  );
}

// ── 员工新增/编辑弹窗 ───────────────────────────────────────────
function EmployeeModal({ employee, units, onClose, onSaved }: {
  employee: Employee | null; units: OrgUnit[]; onClose: () => void; onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: employee?.name || '',
    org_unit_id: employee?.org_unit_id || '',
    title: employee?.title || '',
    level: employee?.level || '',
    employee_type: employee?.employee_type || 'full_time',
    hire_date: employee?.hire_date || '',
    monthly_cost: employee?.monthly_cost != null ? String(employee.monthly_cost) : '',
    status: employee?.status || 'active',
    is_leader: employee?.is_leader ? 1 : 0,
  });
  const [saving, setSaving] = useState(false);
  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) { toast.error('请填写姓名'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        org_unit_id: form.org_unit_id || null,
        monthly_cost: form.monthly_cost === '' ? 0 : Number(form.monthly_cost),
      };
      if (employee?.id) await updateEmployee(employee.id, payload);
      else await createEmployee({ ...payload, external_source: 'feishu' });
      toast.success(employee?.id ? '已更新' : '已新增');
      onSaved();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell title={employee?.id ? '编辑员工' : '新增员工'} onClose={onClose} onSubmit={submit} saving={saving}>
      <div className="grid grid-cols-2 gap-4">
        <Labeled label="姓名 *"><input className={inputCls} value={form.name} onChange={e => set('name', e.target.value)} /></Labeled>
        <Labeled label="所属部门">
          <select className={inputCls} value={form.org_unit_id || ''} onChange={e => set('org_unit_id', e.target.value)}>
            <option value="">未分配</option>
            {units.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </Labeled>
        <Labeled label="职位"><input className={inputCls} value={form.title} onChange={e => set('title', e.target.value)} /></Labeled>
        <Labeled label="职级"><input className={inputCls} value={form.level} onChange={e => set('level', e.target.value)} /></Labeled>
        <Labeled label="用工类型">
          <select className={inputCls} value={form.employee_type} onChange={e => set('employee_type', e.target.value)}>
            {Object.entries(EMPLOYEE_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Labeled>
        <Labeled label="入职日期"><input type="date" className={inputCls} value={form.hire_date || ''} onChange={e => set('hire_date', e.target.value)} /></Labeled>
        <Labeled label="月成本 (元)"><input type="number" className={inputCls} value={form.monthly_cost} onChange={e => set('monthly_cost', e.target.value)} /></Labeled>
        <Labeled label="状态">
          <select className={inputCls} value={form.status} onChange={e => set('status', e.target.value)}>
            <option value="active">在职</option>
            <option value="on_leave">请假</option>
            <option value="resigned">离职</option>
          </select>
        </Labeled>
        <label className="col-span-2 flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={!!form.is_leader} onChange={e => set('is_leader', e.target.checked ? 1 : 0)} />
          设为部门负责人
        </label>
      </div>
    </ModalShell>
  );
}

// ── 部门新增/编辑弹窗 ───────────────────────────────────────────
function DepartmentModal({ unit, units, onClose, onSaved }: {
  unit: OrgUnit | null; units: OrgUnit[]; onClose: () => void; onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: unit?.name || '',
    parent_id: unit?.parent_id || '',
    type: unit?.type || 'department',
    hc_plan: unit?.hc_plan != null ? String(unit.hc_plan) : '',
    description: unit?.description || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) { toast.error('请填写部门名称'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        parent_id: form.parent_id || null,
        hc_plan: form.hc_plan === '' ? null : Number(form.hc_plan),
      };
      if (unit?.id) await updateTalentUnit(unit.id, payload);
      else await createTalentUnit({ ...payload, external_source: 'feishu' });
      toast.success(unit?.id ? '已更新' : '已新增');
      onSaved();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const parentOptions = units.filter(u => u.id !== unit?.id);

  return (
    <ModalShell title={unit?.id ? '编辑部门' : '新增部门'} onClose={onClose} onSubmit={submit} saving={saving}>
      <div className="space-y-4">
        <Labeled label="部门名称 *"><input className={inputCls} value={form.name} onChange={e => set('name', e.target.value)} /></Labeled>
        <div className="grid grid-cols-2 gap-4">
          <Labeled label="上级部门">
            <select className={inputCls} value={form.parent_id || ''} onChange={e => set('parent_id', e.target.value)}>
              <option value="">顶级部门</option>
              {parentOptions.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </Labeled>
          <Labeled label="类型">
            <select className={inputCls} value={form.type} onChange={e => set('type', e.target.value)}>
              <option value="department">部门</option>
              <option value="team">团队</option>
              <option value="squad">小组</option>
            </select>
          </Labeled>
        </div>
        <Labeled label="编制数 (HC)"><input type="number" className={inputCls} value={form.hc_plan} onChange={e => set('hc_plan', e.target.value)} /></Labeled>
        <Labeled label="描述"><textarea className={inputCls} rows={3} value={form.description} onChange={e => set('description', e.target.value)} /></Labeled>
      </div>
    </ModalShell>
  );
}
